import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAppStore } from "@/lib/store";
import RevealOnScroll from "@/components/ui/RevealOnScroll";
import ImmersiveBackground from "@/components/ui/ImmersiveBackground";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import { cn } from "@/lib/utils";

export default function SignupPage() {
  useDocumentMeta({
    title: "Create Your Account",
    description:
      "Join Nerd Multiverse to buy and sell second-hand collectibles, comics, gaming gear and anime memorabilia.",
    path: "/signup",
  });

  const navigate = useNavigate();
  const signup = useAppStore((s) => s.signup);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [wantsToSell, setWantsToSell] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await signup(
        email.trim(),
        password,
        displayName.trim(),
        wantsToSell ? "seller" : "buyer"
      );
      navigate(wantsToSell ? "/dashboard/seller" : "/marketplace");
    } catch {
      setError("Couldn't create that account — the email may already be in use.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative overflow-hidden min-h-dvh flex items-center md:block pt-20 pb-8 md:pt-32 md:pb-24">
      <ImmersiveBackground
        src="/images/hero/signup-v2.webp"
        mobileSrc="/images/hero/signup-mobile-v2.webp"
        focus="50% 24%"
      />
      <div className="nm-container relative w-full">
        <div className="mx-auto w-full max-w-md md:max-w-xl lg:max-w-2xl">
          <RevealOnScroll>
            <span className="hidden md:inline-flex nm-kicker">Join the Multiverse</span>
            <h1 className="md:mt-4 font-display text-2xl md:text-5xl font-black tracking-[-.04em] text-white">
              Create your account
            </h1>

            <div className="nm-glass mt-4 md:mt-5 rounded-2xl p-4 md:p-10">
              <form onSubmit={handleSubmit} className="space-y-3 md:space-y-5">
                <div>
                  <label className="sr-only md:not-sr-only md:block text-sm md:text-base text-[var(--nm-ink-muted)] md:mb-1.5">
                    Name
                  </label>
                  <input
                    required
                    placeholder="Name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="w-full px-4 py-2.5 md:py-4 md:text-lg rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-[var(--nm-ink-muted)] focus:outline-none focus:border-[var(--nm-blue-bright)] transition-colors"
                  />
                </div>
                <div>
                  <label className="sr-only md:not-sr-only md:block text-sm md:text-base text-[var(--nm-ink-muted)] md:mb-1.5">
                    Email
                  </label>
                  <input
                    type="email"
                    required
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-2.5 md:py-4 md:text-lg rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-[var(--nm-ink-muted)] focus:outline-none focus:border-[var(--nm-blue-bright)] transition-colors"
                  />
                </div>
                <div>
                  <label className="sr-only md:not-sr-only md:block text-sm md:text-base text-[var(--nm-ink-muted)] md:mb-1.5">
                    Password
                  </label>
                  <input
                    type="password"
                    required
                    minLength={6}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-2.5 md:py-4 md:text-lg rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-[var(--nm-ink-muted)] focus:outline-none focus:border-[var(--nm-blue-bright)] transition-colors"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => setWantsToSell((v) => !v)}
                  className={cn(
                    "w-full flex items-center justify-between px-4 py-2.5 rounded-lg border text-left transition-colors",
                    wantsToSell
                      ? "border-[var(--nm-blue-bright)] bg-[var(--nm-blue)]/10"
                      : "border-white/10 bg-white/5"
                  )}
                >
                  <span className="text-xs md:text-base text-white pr-2">
                    I want to sell on Nerd Multiverse
                  </span>
                  <span
                    className={cn(
                      "w-5 h-5 rounded-full border flex items-center justify-center shrink-0",
                      wantsToSell ? "bg-[var(--nm-blue-bright)] border-[var(--nm-blue-bright)]" : "border-white/30"
                    )}
                  >
                    {wantsToSell && <span className="w-2 h-2 rounded-full bg-white" />}
                  </span>
                </button>

                {error && <p className="text-sm text-[var(--nm-pink)]">{error}</p>}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 md:py-4 rounded-full bg-white text-[#05070d] text-sm md:text-base font-medium tracking-wide hover:bg-[var(--nm-blue-bright)] hover:text-white transition-colors duration-200 disabled:opacity-50"
                >
                  {submitting ? "CREATING ACCOUNT…" : "CREATE ACCOUNT"}
                </button>
                <p className="text-xs text-[var(--nm-ink-muted)] text-center">
                  By creating an account you agree to our{" "}
                  <Link to="/terms" className="text-white underline underline-offset-4">
                    Terms
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="text-white underline underline-offset-4">
                    Privacy Policy
                  </Link>
                  .
                </p>
              </form>
            </div>

            <p className="mt-4 md:mt-6 text-sm md:text-base text-[var(--nm-ink-muted)]">
              Already have an account?{" "}
              <Link to="/login" className="text-white underline underline-offset-4">
                Sign in
              </Link>
            </p>
          </RevealOnScroll>
        </div>
      </div>
    </div>
  );
}
